import { Layer, Source } from 'react-map-gl';
import { useMemo } from 'react';
import { FeatureCollection, LineString } from 'geojson';
import { featureCollection } from '@turf/helpers';
import { COLOR_MAP_CONDITION_OUTLINE } from '@alltrails/denali/tokens';
import { getChevronsId, getOutlineId, getPolylineId, polylinesTopId } from '../../utils/layers';
import TrailSurfaceTypeSegment from '../../types/TrailSurfaceTypeSegment';
import { decodeLatLngs, reverseCoord } from '../../utils/legacyGeoJSONConversions';
import ChevronLayer from './ChevronLayer';

const uniqueId = 'surface-type';
const sourceId = `${uniqueId}-source`;
const outlineId = getOutlineId(uniqueId);
const polylineId = getPolylineId(uniqueId);
const chevronsId = getChevronsId(uniqueId);

type SurfaceTypePolylineProps = {
  segments: TrailSurfaceTypeSegment[];
};

const SurfaceTypePolyline = ({ segments }: SurfaceTypePolylineProps) => {
  const data = useMemo<FeatureCollection<LineString, { color: string }>>(() => {
    return featureCollection(
      segments.map(({ polyline, surfaceType }) => {
        const decodedPolyline = decodeLatLngs(polyline).map(latLng => reverseCoord(latLng));
        return {
          geometry: { type: 'LineString', coordinates: decodedPolyline },
          properties: { color: surfaceType.color },
          type: 'Feature'
        };
      })
    );
  }, [segments]);

  return (
    <Source id={sourceId} type="geojson" data={data}>
      <ChevronLayer beforeId={polylinesTopId} chevronLayerId={chevronsId} sourceId={sourceId} symbolSpacing={150} />
      <Layer
        beforeId={chevronsId}
        id={polylineId}
        source={sourceId}
        type="line"
        layout={{
          'line-join': 'round',
          'line-cap': 'round'
        }}
        paint={{ 'line-width': 4, 'line-color': ['get', 'color'] }}
      />
      <Layer
        beforeId={polylineId}
        id={outlineId}
        type="line"
        source={sourceId}
        layout={{ 'line-join': 'round', 'line-cap': 'round' }}
        paint={{ 'line-width': 8, 'line-color': COLOR_MAP_CONDITION_OUTLINE }}
      />
    </Source>
  );
};

export default SurfaceTypePolyline;
